import {
  ContentDrawer,
  SettingItem,
  Tooltip,
  SearchIcon,
  OwnerIcon,
  ConfirmDialog,
  UserItem,
} from '@halo/ui-chat-react';
import { Stack, InputGroup, Center, Input, Box, Text } from '@chakra-ui/react';
import type { GroupMember } from '@halo/chat-sdk';
import { GroupMemberRole } from '@halo/chat-sdk';
import { StateEnum } from '@halo/chat-sdk-react';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface TransferGroupOwnerProps {
  trigger: React.ReactNode;
  members: GroupMember[];
  onMemberAvatarClick?: (member: GroupMember) => Promise<void>;
  onTransferClick: (member: GroupMember) => Promise<void>;
}

const TransferGroupOwner = ({
  trigger,
  members,
  onMemberAvatarClick,
  onTransferClick,
}: TransferGroupOwnerProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [transferMember, setTransferMember] = useState({} as GroupMember);
  const [transferLoading, setTransferLoading] = useState(false);

  const isAdmin = (member: GroupMember) => {
    return member.role === 1 || member.role === 2;
  };
  const filteredMembers = useMemo(() => {
    return members.filter(
      (member) =>
        member.name?.toLowerCase().includes(search?.toLowerCase()) &&
        member.state !== StateEnum.DELETE_ACCOUNT
    );
  }, [members, search]);

  const adminMembers = filteredMembers.filter((member) => member.role === GroupMemberRole.ADMIN);
  const normalMembers = filteredMembers.filter(
    (member) => member.role !== GroupMemberRole.OWNER && member.role !== GroupMemberRole.ADMIN
  );

  const handleTransferClick = useCallback((member: GroupMember) => {
    setTransferMember(member);
    setConfirm(true);
  }, []);

  const getRoleLabel = (role: number) => {
    switch (role) {
      case 1:
        return t('chat.groupRoleOwner');
      case 2:
        return t('chat.groupRoleAdmin');
      default:
        return '';
    }
  };

  return (
    <SettingItem>
      <ContentDrawer title={t('chatSettings.transferGroupOwner')} trigger={trigger}>
        <Stack h="full" gap="2">
          <InputGroup
            startElement={
              <Center flexShrink="0" fontSize="md">
                <SearchIcon />
              </Center>
            }
          >
            <Input
              size="sm"
              placeholder={t('filter.searchPlaceholder')}
              bg="bg"
              borderRadius="xl"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          <Box className="scrollHover" borderRadius="xl" flex="1" overflowY="auto">
            {adminMembers.length > 0 && (
              <>
                <Box fontSize="xs" fontWeight="medium" color="fg.muted" py="2.5">
                  {t('chat.groupRoleAdmin')}
                </Box>
                <Box bg="bg" borderRadius="xl" p="1">
                  {adminMembers.map((member, index) => (
                    <UserItem
                      key={member.user_id}
                      {...member}
                      rippleEffect={false}
                      hoverable={member.role === 1 ? false : true}
                      onAvatarClick={() => onMemberAvatarClick?.(member)}
                      userExtraElementRenderer={(isHovered) => (
                        <>
                          {isHovered && member.role === 2 ? (
                            <Tooltip
                              content={t('chatSettings.transferToOwner')}
                              positioning={{ placement: 'top' }}
                            >
                              <Center
                                fontSize="md"
                                color="fg.muted"
                                cursor="pointer"
                                onClick={() => handleTransferClick?.(member)}
                              >
                                <OwnerIcon />
                              </Center>
                            </Tooltip>
                          ) : isAdmin(member) ? (
                            <Center fontSize="xs" fontWeight="bold">
                              <Text color="fg.muted">{getRoleLabel(member.role)}</Text>
                            </Center>
                          ) : null}
                        </>
                      )}
                      borderColor={
                        index === adminMembers.length - 1 ? 'transparent' : 'border.subtle'
                      }
                    />
                  ))}
                </Box>
              </>
            )}

            {/* Normal Members Section */}
            {normalMembers.length > 0 && (
              <>
                <Box fontSize="xs" fontWeight="medium" color="fg.muted" py="2.5">
                  {t('chat.groupMembersTitle')}
                </Box>
                <Box bg="bg" borderRadius="xl" p="1">
                  {normalMembers.map((member, index) => (
                    <UserItem
                      key={member.user_id}
                      {...member}
                      rippleEffect={false}
                      hoverable={true}
                      onAvatarClick={() => onMemberAvatarClick?.(member)}
                      userExtraElementRenderer={(isHovered) => (
                        <>
                          {isHovered ? (
                            <Tooltip
                              content={t('chatSettings.transferToOwner')}
                              positioning={{ placement: 'top' }}
                            >
                              <Center
                                fontSize="md"
                                color="fg.muted"
                                cursor="pointer"
                                onClick={() => handleTransferClick?.(member)}
                              >
                                <OwnerIcon />
                              </Center>
                            </Tooltip>
                          ) : null}
                        </>
                      )}
                      borderColor={
                        index === normalMembers.length - 1 ? 'transparent' : 'border.subtle'
                      }
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Stack>
        <ConfirmDialog
          disabledPortal={false}
          open={confirm}
          onClose={() => setConfirm(false)}
          title={t('chatSettings.transferGroupOwner')}
          subTitle={t('chatSettings.transferGroupOwnerSubTitle', { name: transferMember.name })}
          onOk={async () => {
            setTransferLoading(true);
            await onTransferClick?.(transferMember);
            setTransferLoading(false);
            setConfirm(false);
          }}
          okText={t('buttons.confirm')}
          okButtonProps={{
            loading: transferLoading,
          }}
        ></ConfirmDialog>
      </ContentDrawer>
    </SettingItem>
  );
};

const MemoizedTransferGroupOwner = memo(TransferGroupOwner);
export { MemoizedTransferGroupOwner as TransferGroupOwner };
